import type { StripeSubscription } from '@/types/clinicalAdmin';

export interface SubscriptionFormatSwitch {
  fromProductId: string;
  toProductId: string;
  switchedAt: string | null;
  actor: string | null;
}

function metadataString(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

const PEPTIDE_FORMAT_LABELS: ReadonlyArray<[prefix: string, label: string]> = [
  ['bpc157-injection-', 'BPC-157 injection'],
  ['tb500-injection-', 'TB-500 injection'],
  ['semax-injection-', 'Semax injection'],
  ['semax-nasal-', 'Semax nasal spray'],
  ['kpv-cream-', 'KPV cream'],
  ['epitalon-injection-', 'Epitalon injection'],
  ['motsc-injection-', 'MOTS-c injection'],
];

/** Convert canonical product ids into compact treatment labels for CX staff. */
export function treatmentFormatLabel(productId: string): string {
  const normalized = productId.trim().toLowerCase();
  const peptide = PEPTIDE_FORMAT_LABELS.find(([prefix]) =>
    normalized.startsWith(prefix),
  );
  // Peptides come in several strengths and vial sizes per form. Show them,
  // or a strength switch reads as "from X to X". Anything else under the
  // prefix (a blend) falls through to the generic label.
  const strength =
    peptide &&
    /^(\d+(?:_\d+)?)mg-ml-(\d+)ml$/.exec(normalized.slice(peptide[0].length));
  if (peptide && strength) {
    return `${peptide[1]} ${strength[1].replace('_', '.')}mg/mL, ${strength[2]}mL`;
  }
  if (normalized.startsWith('glutathione-injection'))
    return 'Glutathione injection';
  if (normalized.startsWith('glutathione-nasal'))
    return 'Glutathione nasal spray';
  if (normalized.startsWith('nad-injection')) return 'NAD+ injection';
  if (normalized.startsWith('nad-nasal')) return 'NAD+ nasal spray';
  if (normalized.startsWith('sermorelin-injection'))
    return 'Sermorelin injection';
  if (
    normalized.startsWith('sermorelin-sublingual') ||
    normalized.endsWith('-sl')
  )
    return 'Sermorelin sublingual';
  return productId.replace(/-/g, ' ');
}

/** Stripe stamps this provenance whenever the support/admin switch flow runs. */
export function subscriptionFormatSwitch(
  subscription: StripeSubscription,
): SubscriptionFormatSwitch | null {
  const fromProductId = metadataString(
    subscription.metadata,
    'format_switch_from',
  );
  const toProductId = metadataString(
    subscription.metadata,
    'systemlabs_product_id',
  );
  if (!fromProductId || !toProductId || fromProductId === toProductId)
    return null;
  return {
    fromProductId,
    toProductId,
    switchedAt: metadataString(subscription.metadata, 'format_switch_at'),
    actor: metadataString(subscription.metadata, 'format_switch_actor'),
  };
}

export function isPreviousSubscriptionFormat(
  groupProductId: string,
  subscription: StripeSubscription,
): boolean {
  const transition = subscriptionFormatSwitch(subscription);
  return transition?.fromProductId === groupProductId;
}
